import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import LanguageToggle from './LanguageToggle';
import { Shield, Lock, Eye, EyeOff, Users } from 'lucide-react';

import { BACKEND_URL } from '../utils/api';
const API = `${BACKEND_URL}/api`;

function AdminLogin() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    totp_code: ''
  });
  const [isAffiliate, setIsAffiliate] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isAffiliate) {
        // Affiliate login
        const response = await axios.post(`${API}/affiliate/login`, {
          email: formData.email,
          password: formData.password
        });
        
        // Store affiliate session
        localStorage.setItem('affiliate_code', response.data.affiliate_code);
        localStorage.setItem('affiliate_name', response.data.name);
        localStorage.setItem('affiliate_email', response.data.email);
        navigate('/affiliate/dashboard');
      } else {
        // Admin login
        const payload = {
          email: formData.email,
          password: formData.password
        };

        // Add TOTP code if 2FA is required
        if (requires2FA) {
          payload.totp_code = formData.totp_code;
        }

        const response = await axios.post(`${API}/admin/login`, payload);

        if (response.data.requires_2fa) {
          // Need 2FA code
          setRequires2FA(true);
          setFormData({ ...formData, password: '' });
        } else {
          // Login successful
          localStorage.setItem('admin_token', response.data.access_token);
          localStorage.setItem('admin_user', JSON.stringify(response.data.admin));
          navigate('/admin/dashboard');
        }
      }
    } catch (err) {
      // Handle validation errors (422)
      if (err.response?.status === 422 && err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          // Pydantic validation error format
          const errorMessages = detail.map(e => e.msg || e.message).join(', ');
          setError(errorMessages || t('admin.errValidation'));
        } else {
          setError(detail);
        }
      } else {
        setError(err.response?.data?.detail || t('admin.errLoginFail'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4 relative">
      <div className="absolute top-4 right-4 z-10">
        <LanguageToggle variant="dark" />
      </div>
      <Card className="w-full max-w-md border-slate-700 shadow-2xl">
        <CardHeader className="text-center">
          <div className={`mx-auto mb-4 w-16 h-16 ${isAffiliate ? 'bg-green-600' : 'bg-blue-600'} rounded-full flex items-center justify-center transition-colors`}>
            {isAffiliate ? <Users className="h-8 w-8 text-white" /> : <Shield className="h-8 w-8 text-white" />}
          </div>
          <CardTitle className="text-2xl">{isAffiliate ? t('admin.headingPartner') : t('admin.headingAdmin')}</CardTitle>
          <CardDescription>
            {requires2FA ? t('admin.sub2fa') : isAffiliate ? t('admin.subPartner') : t('admin.subAdmin')}
          </CardDescription>
          {!requires2FA && (
            <button
              type="button"
              onClick={() => { setIsAffiliate(!isAffiliate); setError(''); setFormData({ email: '', password: '', totp_code: '' }); }}
              className="mt-3 text-xs text-slate-500 hover:text-slate-700 underline"
              data-testid="toggle-admin-partner"
            >
              {isAffiliate ? t('admin.switchToAdmin') : t('admin.switchToPartner')}
            </button>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            {!requires2FA ? (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">{t('admin.emailLabel')}</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={t('admin.emailPlaceholder')}
                    required
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">{t('admin.passwordLabel')}</label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder={t('admin.passwordPlaceholder')}
                      required
                      disabled={loading}
                      autoComplete="current-password"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      tabIndex="-1"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div>
                <label className="block text-sm font-medium mb-2">
                  {t('admin.totpLabel')}
                </label>
                <Input
                  type="text"
                  name="totp_code"
                  value={formData.totp_code}
                  onChange={handleInputChange}
                  placeholder="000000"
                  maxLength="6"
                  required
                  disabled={loading}
                  autoFocus
                />
                <p className="text-xs text-slate-500 mt-1">
                  {t('admin.totpHelp')}
                </p>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className={`w-full ${isAffiliate ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}
              data-testid="admin-login-submit"
            >
              <Lock className="h-4 w-4 mr-2" />
              {loading ? t('admin.btnLoggingIn') : requires2FA ? t('admin.btnVerify2fa') : isAffiliate ? t('admin.btnLoginPartner') : t('admin.btnLogin')}
            </Button>

            {!requires2FA && !isAffiliate && (
              <div className="text-center mt-4">
                <Link
                  to="/admin/password-reset"
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  {t('admin.forgotPassword')}
                </Link>
              </div>
            )}

            {!requires2FA && isAffiliate && (
              <div className="text-center mt-4 space-y-2">
                <Link
                  to="/affiliate/password-reset"
                  className="text-sm text-green-600 hover:text-green-700 font-medium block"
                  data-testid="affiliate-forgot-password"
                >
                  {t('admin.forgotPassword')}
                </Link>
                <p className="text-sm text-slate-500">
                  {t('admin.notPartnerYet')}{' '}
                  <Link to="/affiliate" className="text-green-600 hover:text-green-700 font-medium">
                    {t('admin.applyNow')}
                  </Link>
                </p>
              </div>
            )}

            {requires2FA && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setRequires2FA(false);
                  setFormData({ email: '', password: '', totp_code: '' });
                  setError('');
                }}
                disabled={loading}
                className="w-full"
              >
                {t('admin.backToLogin')}
              </Button>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default AdminLogin;
